import i18n from "../i18n";
import siteConfig from "../siteConfig";
const t = (key, opts) => i18n.t(key, opts);

/**
 * Kullanıcı kimlik doğrulaması ile ilgili işlemleri yöneten servis
 * Cookie-based authentication kullanır
 */
class AuthService {
  constructor() {
    // LocalStorage'dan kullanıcı bilgisini çek (sayfa yenilendiğinde kaybolmaması için)
    this.user = JSON.parse(localStorage.getItem("user")) || null;
    this.listeners = [];

    // API yapılandırması
    this.API_CONFIG = {
      BASE_URL: siteConfig.apiBaseUrl,
      ENDPOINTS: {
        GOOGLE_URL: "/auth/google/url",
        USER_INFO: "/auth/user", // Kullanıcı bilgilerini almak için
        LOGOUT: "/auth/logout", // Çıkış yapmak için
        ADD_LINK: "/user/link", // Link eklemek için
        DELETE_LINK: "/user/link", // Link silmek için
        GET_LINKS: "/user/links", // Linkleri almak için
        CHANGE_USERNAME: "/user/change-username", // Kullanıcı adını değiştirmek için
        STATISTICS: "/statistics", // İstatistikleri almak için
        PHOTOS: "/user/photos", // Kullanıcının yüklediği fotoğraflar
      },
    };

    // Sayfa yüklendiğinde cookie'leri kontrol et
    this._checkAuthStatus();
  }

  /**
   * HttpOnly cookie'ler ile auth durumunu belirle
   * @private
   */
  async _checkAuthStatus() {
    try {
      console.log("🔍 Checking auth status with httpOnly cookies...");

      // HttpOnly cookie'ler ile backend'ten kullanıcı durumunu kontrol et

      // Backend'ten kullanıcı bilgilerini al (httpOnly cookie'ler otomatik gönderilir)
      const response = await fetch(
        `${this.API_CONFIG.BASE_URL}${this.API_CONFIG.ENDPOINTS.USER_INFO}`,
        {
          method: "GET",
          credentials: "include", // HttpOnly cookie'leri dahil et
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      if (response.ok) {
        const userData = await response.json();

        this.user = {
          id: userData.id || userData.userId,
          username: userData.username || userData.name || "Google User",
          email: userData.email,
          score: userData.score || 0,
          links: userData.links || [],
          loginTime: new Date().toISOString(),
          provider: "google",
        };
        localStorage.setItem("user", JSON.stringify(this.user));
        this._notifyListeners();
        console.log("✅ User logged in successfully with httpOnly cookies");
      } else {
        // HttpOnly cookie'ler geçersiz veya yok
        console.log(
          "❌ Auth failed with httpOnly cookies, status:",
          response.status,
        );

        this.user = null;
        localStorage.removeItem("user");
        this._notifyListeners();
      }
    } catch (error) {
      console.error("❌ Auth status check error with httpOnly cookies:", error);
      this.user = null;
      localStorage.removeItem("user");
      this._notifyListeners();
    }
  }

  /**
   * Google OAuth URL'ini al ve kullanıcıyı yönlendir
   * @returns {Promise<string>} - Google OAuth URL'i
   */
  async getGoogleAuthUrl() {
    try {
      const response = await fetch(
        `${this.API_CONFIG.BASE_URL}${this.API_CONFIG.ENDPOINTS.GOOGLE_URL}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({
          message: t("errors.googleOAuthError"),
        }));
        throw new Error(errorData.message || t("errors.googleOAuthError"));
      }

      const data = await response.json();
      return data.url;
    } catch (error) {
      console.error("Google OAuth URL hatası:", error);
      throw error;
    }
  }

  /**
   * Google OAuth ile giriş yap
   */
  async loginWithGoogle() {
    try {
      const authUrl = await this.getGoogleAuthUrl();
      // Kullanıcıyı Google OAuth sayfasına yönlendir
      window.location.href = authUrl;
    } catch (error) {
      console.error("Google login hatası:", error);
      throw error;
    }
  }

  /**
   * Google callback'ten sonra auth durumunu kontrol et
   * Backend cookie'leri set etmiş olmalı
   */
  async handleGoogleCallback() {
    try {
      // Auth durumunu tekrar kontrol et (cookie'ler set edilmiş olmalı)
      await this._checkAuthStatus();

      if (this.user) {
        return { user: this.user };
      } else {
        throw new Error(t("errors.googleLoginFailed"));
      }
    } catch (error) {
      console.error("Google callback error:", error);
      throw error;
    }
  }

  /**
   * Kullanıcıyı çıkış yaptır
   */
  async logout() {
    try {
      // Backend'e logout isteği gönder (cookie'leri temizler)
      await fetch(
        `${this.API_CONFIG.BASE_URL}${this.API_CONFIG.ENDPOINTS.LOGOUT}`,
        {
          method: "POST",
          credentials: "include", // Cookie'leri dahil et
        },
      );
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      // Her durumda local state'i temizle
      this.user = null;
      localStorage.removeItem("user");
      this._notifyListeners();
    }
  }

  /**
   * Kullanıcının giriş yapmış olup olmadığını kontrol et
   * @returns {boolean} - Kullanıcı giriş yapmışsa true, yapmamışsa false
   */
  isLoggedIn() {
    return !!this.user;
  }

  /**
   * Mevcut kullanıcı bilgilerini döndür
   * @returns {Object|null} - Kullanıcı bilgileri
   */
  getCurrentUser() {
    return this.user;
  }

  /**
   * Kullanıcı durumu değiştiğinde tetiklenecek bir dinleyici ekle
   * @param {Function} listener - Çağrılacak fonksiyon
   */
  addAuthListener(listener) {
    if (typeof listener === "function" && !this.listeners.includes(listener)) {
      this.listeners.push(listener);
    }
  }

  /**
   * Bir dinleyiciyi kaldır
   * @param {Function} listener - Kaldırılacak dinleyici fonksiyonu
   */
  removeAuthListener(listener) {
    this.listeners = this.listeners.filter((l) => l !== listener);
  }

  /**
   * Kullanıcıya yeni link ekle
   * @param {string} link - Eklenecek link
   * @returns {Promise<Object>} - API yanıtı
   */
  async addLink(link) {
    try {
      const response = await fetch(
        `${this.API_CONFIG.BASE_URL}${this.API_CONFIG.ENDPOINTS.ADD_LINK}`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            accept: "application/json",
          },
          body: JSON.stringify({ link }),
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({
          message: t("errors.linkAddError"),
        }));
        throw new Error(errorData.message || t("errors.linkAddError"));
      }

      const data = await response.json();

      // Kullanıcı bilgilerini manuel olarak güncelle (API çağrısı yapmadan)
      if (this.user) {
        this.user.links = this.user.links || [];
        this.user.links.push(link);
        localStorage.setItem("user", JSON.stringify(this.user));
        this._notifyListeners();
      }

      return data;
    } catch (error) {
      console.error("Link ekleme hatası:", error);
      throw error;
    }
  }

  /**
   * Kullanıcının linklerini getir
   * @returns {Promise<Array>} - Kullanıcının linkleri
   */
  async getLinks() {
    try {
      const response = await fetch(
        `${this.API_CONFIG.BASE_URL}${this.API_CONFIG.ENDPOINTS.GET_LINKS}`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            accept: "application/json",
          },
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({
          message: t("errors.linkGetError"),
        }));
        throw new Error(errorData.message || t("errors.linkGetError"));
      }

      const data = await response.json();

      // Kullanıcı bilgilerini güncelle
      if (this.user) {
        this.user.links = data.links || [];
        localStorage.setItem("user", JSON.stringify(this.user));
        this._notifyListeners();
      }

      return data.links || [];
    } catch (error) {
      console.error("Link getirme hatası:", error);
      throw error;
    }
  }

  /**
   * Kullanıcıdan link sil
   * @param {string} link - Silinecek link
   * @returns {Promise<Object>} - API yanıtı
   */
  async deleteLink(link) {
    try {
      const response = await fetch(
        `${this.API_CONFIG.BASE_URL}${this.API_CONFIG.ENDPOINTS.DELETE_LINK}`,
        {
          method: "DELETE",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            accept: "application/json",
          },
          body: JSON.stringify({ link }),
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({
          message: t("errors.linkDeleteError"),
        }));
        throw new Error(errorData.message || t("errors.linkDeleteError"));
      }

      const data = await response.json();

      // Kullanıcı bilgilerini manuel olarak güncelle (API çağrısı yapmadan)
      if (this.user && this.user.links) {
        this.user.links = this.user.links.filter((l) => l !== link);
        localStorage.setItem("user", JSON.stringify(this.user));
        this._notifyListeners();
      }

      return data;
    } catch (error) {
      console.error("Link silme hatası:", error);
      throw error;
    }
  }

  /**
   * Kullanıcı adını değiştir
   * @param {string} username - Yeni kullanıcı adı
   * @returns {Promise<Object>} - API yanıtı
   */
  async changeUsername(username) {
    try {
      const response = await fetch(
        `${this.API_CONFIG.BASE_URL}${this.API_CONFIG.ENDPOINTS.CHANGE_USERNAME}`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            accept: "application/json",
          },
          body: JSON.stringify({ username }),
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({
          message: t("errors.usernameChangeError"),
        }));
        throw new Error(errorData.message || t("errors.usernameChangeError"));
      }

      const data = await response.json();

      // Local kullanıcı bilgisini güncelle
      if (this.user) {
        this.user.username = username;
        localStorage.setItem("user", JSON.stringify(this.user));
        this._notifyListeners();
      }

      return data;
    } catch (error) {
      console.error("Kullanıcı adı değiştirme hatası:", error);
      throw error;
    }
  }

  /**
   * Tüm dinleyicilere bildirim gönder
   * @private
   */
  _notifyListeners() {
    this.listeners.forEach((listener) => {
      try {
        listener(this.user);
      } catch (error) {
        console.error("Auth listener error:", error);
      }
    });
  }

  /**
   * Kullanıcı istatistiklerini getirir
   * @returns {Promise<any>} - İstatistik verileri
   */
  async getStatistics() {
    if (!this.isLoggedIn()) {
      throw new Error(t("errors.authRequired"));
    }

    try {
      const url = new URL(
        `${this.API_CONFIG.BASE_URL}${this.API_CONFIG.ENDPOINTS.STATISTICS}`,
      );

      // Username varsa query parametresi olarak ekle
      if (this.user && this.user.username) {
        url.searchParams.append("user", this.user.username);
      }

      const response = await fetch(url.toString(), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "include", // Cookie'leri dahil et
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error(t("errors.authRequired"));
        }
        throw new Error(t("errors.statsLoadError"));
      }

      const result = await response.json();

      // Backend'den gelen data null ise boş array döndür
      if (!result.data || result.data === null) {
        return { data: [] };
      }

      return result;
    } catch (error) {
      console.error("İstatistikler yüklenirken hata:", error);
      throw error;
    }
  }

  /**
   * Kullanıcının yüklediği tüm fotoğrafları getirir (tek seferde). Sayfalama frontend'de.
   * @returns {Promise<{data: Array, total: number, totalScore: number}>} - Tüm fotoğraflar, toplam sayı, toplam skor
   */
  async getPhotos() {
    if (!this.isLoggedIn()) {
      throw new Error(t("errors.authRequired"));
    }

    try {
      const url = `${this.API_CONFIG.BASE_URL}${this.API_CONFIG.ENDPOINTS.PHOTOS}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "include",
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error(t("errors.authRequired"));
        }
        throw new Error(t("errors.photosLoadError"));
      }

      const result = await response.json();
      return {
        data: result.data || [],
        total: result.total ?? 0,
        totalScore: result.total_score ?? 0,
      };
    } catch (error) {
      console.error("Fotoğraflar yüklenirken hata:", error);
      throw error;
    }
  }

  /**
   * Belirtilen kullanıcının public profil bilgisini getirir (username, links)
   * @param {string} username - Profili görüntülenecek kullanıcı adı
   * @returns {Promise<{username: string, links: string[]}>}
   */
  async getProfileByUsername(username) {
    if (!username?.trim()) {
      throw new Error(t("errors.usernameRequired"));
    }
    try {
      const url = `${this.API_CONFIG.BASE_URL}/user/profile/${encodeURIComponent(username.trim())}`;
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "include",
      });
      if (!response.ok) {
        if (response.status === 404) throw new Error(t("errors.userNotFound"));
        // Backend public olmalı; auth engeli gelirse boş profil döndür.
        if (response.status === 401)
          return { username: username.trim(), links: [] };
        throw new Error(t("errors.profileLoadError"));
      }
      const result = await response.json();
      return {
        username: result.username,
        links: result.links || [],
      };
    } catch (error) {
      console.error("Profil yüklenirken hata:", error);
      throw error;
    }
  }

  /**
   * Belirtilen kullanıcının tüm fotoğraflarını getirir (başka kullanıcı profili için). Sayfalama frontend'de.
   * @param {string} username - Kullanıcı adı
   * @returns {Promise<{data: Array, total: number, totalScore: number}>}
   */
  async getPhotosByUsername(username) {
    if (!username?.trim()) {
      throw new Error(t("errors.usernameRequired"));
    }
    try {
      const url = `${this.API_CONFIG.BASE_URL}/user/profile/${encodeURIComponent(username.trim())}/photos`;
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "include",
      });
      if (!response.ok) {
        if (response.status === 404) throw new Error(t("errors.userNotFound"));
        // Backend public olmalı; auth engeli gelirse boş galeri döndür.
        if (response.status === 401)
          return { data: [], total: 0, totalScore: 0 };
        throw new Error(
          `Fotoğraflar yüklenirken hata: HTTP ${response.status}`,
        );
      }
      const result = await response.json();
      return {
        data: result.data || [],
        total: result.total ?? 0,
        totalScore: result.total_score ?? 0,
      };
    } catch (error) {
      console.error("Fotoğraflar yüklenirken hata:", error);
      throw error;
    }
  }

  /**
   * Bir fotoğrafın kullanıcı haritası önceliğini günceller. -1 = haritada gösterme; 0-5 = haritada göster, yüksek önce.
   * @param {string} photoId - Fotoğraf ID'si
   * @param {number} userPriority - -1 ile 5 arası (varsayılan 0)
   */
  async setPhotoUserPriority(photoId, userPriority) {
    if (!this.isLoggedIn()) {
      throw new Error(t("errors.authRequired"));
    }
    if (!photoId) {
      throw new Error(t("errors.photoIdRequired"));
    }
    const priority = Number(userPriority);
    if (Number.isNaN(priority) || priority < -1 || priority > 5) {
      throw new Error(t("errors.priorityRange"));
    }

    const url = `${this.API_CONFIG.BASE_URL}/user/photos/${encodeURIComponent(photoId)}/user-priority`;

    const response = await fetch(url, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      credentials: "include",
      body: JSON.stringify({ user_priority: priority }),
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error(t("errors.authRequired"));
      }
      throw new Error(t("errors.apiError", { status: response.status }));
    }

    return response.json();
  }

  /**
   * Belirtilen kullanıcının istatistiklerini getirir (başka kullanıcı profili için)
   * @param {string} username - Kullanıcı adı
   * @returns {Promise<any>} - İstatistik verileri
   */
  async getStatisticsByUser(username) {
    if (!this.isLoggedIn()) {
      throw new Error(t("errors.authRequired"));
    }
    if (!username?.trim()) {
      return { data: [] };
    }
    try {
      const url = new URL(
        `${this.API_CONFIG.BASE_URL}${this.API_CONFIG.ENDPOINTS.STATISTICS}`,
      );
      url.searchParams.set("user", username.trim());
      const response = await fetch(url.toString(), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "include",
      });
      if (!response.ok) {
        if (response.status === 401) throw new Error(t("errors.authRequired"));
        return { data: [] };
      }
      const result = await response.json();
      return { data: result?.data ?? [] };
    } catch (error) {
      console.error("İstatistikler yüklenirken hata:", error);
      return { data: [] };
    }
  }
}

// Singleton servis instance'ı oluştur
const authService = new AuthService();

export default authService;
